import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import { useAuth } from '../../hooks/useAuth.js';
import Card from './Card.jsx';
import Button from './Button.jsx';
import { HandoverFields, formFromInventory, errorText, itemKey, fmtDate, NONE } from './HandoverModal.jsx';

// On your own profile: prepare the handover of your functions and open work when you stop.
// Saved as a draft; "klaarzetten" tells the member admins, who check and carry it out.

export default function HandoverDraftCard() {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const load = () => api('/api/handovers/draft').then(d => { setData(d); setForm(formFromInventory(d)); if (d.draft) setOpen(true); }).catch(() => setData(null));
  useEffect(() => {
    load();
    api('/api/users/members').then(list => setMembers(list.filter(m => m.id !== auth.userId))).catch(() => {});
  }, [auth.userId, auth.activeClubId]);

  if (!data || !form || (!data.items.length && !data.draft)) return null;

  async function save(submit) {
    setBusy(true); setError(null); setSaved(null);
    const items = {};
    for (const i of data.items) {
      const o = form.overrides[itemKey(i)];
      const entry = {};
      if (o === NONE) entry.to_user_id = null; else if (o) entry.to_user_id = Number(o);
      if (form.remarks[itemKey(i)]?.trim()) entry.remark = form.remarks[itemKey(i)];
      if (Object.keys(entry).length) items[itemKey(i)] = entry;
    }
    // Only notes that differ from the latest one at the function
    const notes = Object.fromEntries(Object.entries(form.notes).filter(([cat, text]) =>
      text.trim() && text.trim() !== (data.notes.find(n => String(n.category_id) === cat)?.note || '')));
    try {
      const draft = await api('/api/handovers/draft', {
        method: 'PUT',
        body: JSON.stringify({ to_user_id: form.successor ? Number(form.successor) : null, note: form.note, items, notes, submit }),
      });
      setData(d => ({ ...d, draft }));
      setSaved(submit ? 'Klaargezet: de ledenbeheerders krijgen een melding.' : 'Concept bewaard.');
    } catch (err) { setError(errorText(err)); }
    setBusy(false);
  }

  async function remove() {
    if (!confirm('Je voorbereide overdracht verwijderen?')) return;
    try { await api('/api/handovers/draft', { method: 'DELETE' }); setOpen(false); setSaved(null); await load(); } catch (err) { setError(errorText(err)); }
  }

  const draft = data.draft;
  return (
    <Card title="🤝 Overdracht voorbereiden">
      {!open ? (
        <div style="display:flex;flex-direction:column;gap:8px;align-items:flex-start">
          <div class="text-sm text-muted">Stop je met een functie? Zet klaar wat je opvolger moet weten en wie wat overneemt. Een ledenbeheerder rondt de overdracht af.</div>
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Overdracht voorbereiden</Button>
        </div>
      ) : (
        <div style="display:flex;flex-direction:column;gap:14px">
          {draft?.submitted_at && <div class="text-sm" style="color:var(--color-success,#16a34a)">✓ Klaargezet op {fmtDate(draft.submitted_at)}. Je kunt nog aanpassen tot een beheerder de overdracht uitvoert.</div>}
          <HandoverFields inv={data} members={members} form={form} setForm={setForm} ownerName="Je" noSuccessorLabel="Voorgestelde opvolger" alwaysOpen />
          {error && <div class="alert alert-error">{error}</div>}
          {saved && <div class="text-sm" style="color:var(--color-success,#16a34a)">{saved}</div>}
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            <Button size="sm" variant="secondary" onClick={() => save(false)} disabled={busy}>Concept bewaren</Button>
            <Button size="sm" onClick={() => save(true)} disabled={busy || !form.successor}>{draft?.submitted_at ? 'Bijwerken' : 'Klaarzetten voor beheerder'}</Button>
            {draft && <Button size="sm" variant="secondary" onClick={remove} disabled={busy}>Verwijderen</Button>}
          </div>
          {!form.successor && <div class="text-sm text-muted">Kies een voorgestelde opvolger om klaar te zetten.</div>}
        </div>
      )}
    </Card>
  );
}
